import React from "react";
import { useTranslation } from "react-i18next";
import { secondaryCriteria } from "@planzone/dto";
import { events } from "@planzone/events";
import { useMessage } from "@components/useMessage";

import { Checkbox } from "@common/checkbox";
import { Button } from "@common/button";
import { Criteria } from "../../../criteria";
import { Props } from "../types";
import { AccessInput } from "../accessInput";
import { NameInput } from "../nameInput";
import styles from "./styles.scss";

function ListForm({
  formData,
  type,
  isCreating: isCreatingList,
  edit,
  submit,
  remove,
  isOption,
}: Props) {
  const { t } = useTranslation();
  const [isCreating, setIsCreating] = React.useState(false);
  const [optionFormData, setOptionFormData] = React.useState({
    ...secondaryCriteria,
    hasChanged: true,
    uuid: "",
  });
  const addOption = () => {
    setIsCreating(true);
    setOptionFormData({
      ...secondaryCriteria,
      type: "text",
      uuid: "",
      hasChanged: true,
      accessRoles: {
        read: [
          "pz_admin",
          "pz_under_admin",
          "pz_user",
          "pz_visitor",
          "pr_manager",
          "pr_worker",
          "pr_visitor",
        ],
        write: [
          "pz_admin",
          "pz_under_admin",
          "pz_user",
          "pr_manager",
          "pr_worker",
        ],
      },
    });
  };

  useMessage(
    (event) =>
      event.type === events.editSecondaryCriteriaTemplateSucceeded &&
      setIsCreating(false),
  );

  return (
    <div className={styles.form}>
      <div className={styles.group}>
        <NameInput
          value={formData}
          input={(value) => edit({ ...formData, ...value })}
        />
      </div>
      <div className={styles.group}>
        <div className={styles.row}>
          <p className={styles.label}>
            {t("secondaryCriteriaTemplate.form.options.label")}
          </p>
          <Button type="submit" className={styles.button} press={addOption}>
            {t("secondaryCriteriaTemplate.add")}
          </Button>
        </div>
        <div className={styles.options}>
          {isCreating && (
            <div className={styles.criteria}>
              <Criteria
                criteria={{
                  ...optionFormData,
                  originalRef: optionFormData,
                  options: [],
                }}
                type={type}
                isOption
                isCreating
                edit={(option) =>
                  setOptionFormData({ ...optionFormData, ...option })
                }
                submit={(option) => {
                  if (isCreatingList) {
                    edit({
                      ...formData,
                      options: [{ ...option, uuid: "" }, ...formData.options],
                    });
                    setIsCreating(false);
                  } else {
                    submit &&
                      submit({
                        ...formData,
                        options: [{ ...option, uuid: "" }, ...formData.options],
                      });
                  }
                }}
                cancel={() => setIsCreating(false)}
              />
            </div>
          )}
          {formData.options.map((criteria, index) => (
            <div key={criteria.uuid} className={styles.criteria}>
              <Criteria
                criteria={{ ...criteria, options: [] }}
                type={type}
                edit={(option) =>
                  edit({
                    ...formData,
                    options: [
                      ...formData.options.slice(0, index),
                      { ...criteria, ...option },
                      ...formData.options.slice(index + 1),
                    ],
                  })
                }
                reset={
                  !isCreatingList
                    ? () =>
                        edit({
                          ...formData,
                          options: [
                            ...formData.options.slice(0, index),
                            { ...criteria, ...criteria.originalRef },
                            ...formData.options.slice(index + 1),
                          ],
                        })
                    : undefined
                }
                submit={(option) =>
                  isCreatingList
                    ? edit({
                        ...formData,
                        options: [
                          ...formData.options.slice(0, index),
                          { ...criteria, ...option },
                          ...formData.options.slice(index + 1),
                        ],
                      })
                    : submit &&
                      submit({
                        ...formData,
                        options: [
                          ...formData.options.slice(0, index),
                          { ...criteria, ...option },
                          ...formData.options.slice(index + 1),
                        ],
                      })
                }
                remove={remove ? () => remove(criteria) : undefined}
                cancel={
                  isCreatingList
                    ? () =>
                        edit({
                          ...formData,
                          options: [
                            ...formData.options.slice(0, index),
                            ...formData.options.slice(index + 1),
                          ],
                        })
                    : undefined
                }
                isOption
              />
            </div>
          ))}
          {formData.options.length === 0 && !isCreating && (
            <div className={styles.addButton} onClick={addOption}>
              <p className={styles.addButtonIcon}>+</p>
            </div>
          )}
        </div>
      </div>
      {!isOption && (
        <>
          <div className={styles.group}>
            <div
              className={styles.attribute}
              onClick={() =>
                edit({
                  ...formData,
                  attr: {
                    ...formData.attr,
                    multiple:
                      formData.attr.multiple === "true" ? "false" : "true",
                  },
                })
              }
            >
              <Checkbox isChecked={formData.attr.multiple === "true"} />
              <p>{t("secondaryCriteriaTemplate.form.options.multiple")}</p>
            </div>
            {type === "project" && (
              <div
                className={styles.attribute}
                onClick={() =>
                  edit({
                    ...formData,
                    attr: {
                      ...formData.attr,
                      required:
                        formData.attr.required === "true" ? "false" : "true",
                    },
                  })
                }
              >
                <Checkbox isChecked={formData.attr.required === "true"} />
                <p>{t("secondaryCriteriaTemplate.form.options.required")}</p>
              </div>
            )}
          </div>
          <div className={styles.group}>
            <AccessInput
              value={formData.accessRoles}
              input={(accessRoles) => edit({ ...formData, accessRoles })}
            />
          </div>
        </>
      )}
    </div>
  );
}

export { ListForm };
